import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, MoveHorizontal } from "lucide-react";
import { useT } from "@/i18n";

const SPEED = 28; // px per second while auto-scrolling
const TOUCH_RESUME = 5000; // ms after the last touch before auto-scroll resumes

// Horizontal carousel: swipe on phones (native scroll + snap), mouse drag on desktop, arrows, keyboard
// arrows and a progress bar. Each child's photo drifts with scroll (--px, see .product-parallax).
// autoplay: scrolls slowly on its own while in view; stops on hover, touch, drag or focus.
export const DragCarousel = ({ id, count, label, autoplay = false, children }) => {
  const t = useT();
  const ref = useRef(null);
  const drag = useRef({ on: false, x: 0, left: 0, moved: false });
  const auto = useRef({ hover: false, touch: 0, focus: false, visible: false, pos: 0, wait: 0 });
  const [state, setState] = useState({ p: 0, start: true, end: false, idx: 0 });
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const mid = el.getBoundingClientRect().left + el.clientWidth / 2;
    let idx = 0;
    let best = Infinity;
    [...el.children].forEach((c, i) => {
      const r = c.getBoundingClientRect();
      const d = (r.left + r.width / 2 - mid) / el.clientWidth;
      c.style.setProperty("--px", d.toFixed(3));
      if (Math.abs(d) < best) { best = Math.abs(d); idx = i; }
    });
    setState({ p: max > 0 ? el.scrollLeft / max : 1, start: el.scrollLeft < 4, end: el.scrollLeft > max - 4, idx });
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [update]);

  useEffect(() => {
    const move = (e) => {
      const d = drag.current;
      if (!d.on) return;
      const dx = e.clientX - d.x;
      if (Math.abs(dx) > 6) d.moved = true;
      ref.current.scrollLeft = d.left - dx;
    };
    const up = () => {
      if (!drag.current.on) return;
      drag.current.on = false;
      setDragging(false);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!autoplay || !el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const io = new IntersectionObserver(([e]) => { auto.current.visible = e.intersectionRatio >= 0.15; }, { threshold: [0, 0.15, 0.3, 0.6] });
    io.observe(el);
    let raf = 0;
    let last = performance.now();
    let was = false;
    const tick = (now) => {
      const a = auto.current;
      const dt = Math.min(now - last, 64);
      last = now;
      const go = a.visible && !a.hover && !a.focus && !drag.current.on && now - a.touch > TOUCH_RESUME;
      if (go !== was) { was = go; setRunning(go); a.pos = el.scrollLeft; }
      if (go) {
        const max = el.scrollWidth - el.clientWidth;
        if (a.wait > now) {
          // pause at the end, then glide back to the first card
        } else if (el.scrollLeft >= max - 1) {
          a.wait = now + 3200;
          setTimeout(() => { el.scrollTo({ left: 0, behavior: "smooth" }); a.pos = 0; }, 1500);
        } else {
          if (Math.abs(el.scrollLeft - a.pos) > 2) a.pos = el.scrollLeft;
          a.pos += (SPEED * dt) / 1000;
          el.scrollLeft = a.pos;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [autoplay]);

  const onDown = (e) => {
    if (e.pointerType !== "mouse") { auto.current.touch = performance.now(); return; }
    if (e.button !== 0 || e.target.closest("button")) return;
    drag.current = { on: true, x: e.clientX, left: ref.current.scrollLeft, moved: false };
    setDragging(true);
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) { e.stopPropagation(); e.preventDefault(); drag.current.moved = false; }
  };
  const step = (dir) => {
    const el = ref.current;
    const card = el.children[0];
    auto.current.touch = performance.now();
    el.scrollBy({ left: dir * (card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8), behavior: "smooth" });
  };
  // with autoplay, desktop scrolls freely (no jump to a card when the mouse stops it); phones keep snapping
  const snap = dragging || running ? "" : autoplay ? "max-md:snap-x max-md:snap-mandatory" : "snap-x snap-mandatory";

  return (
    <div
      className="mt-16"
      onPointerEnter={(e) => { if (e.pointerType === "mouse") auto.current.hover = true; }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") auto.current.hover = false; }}
      onTouchStart={() => { auto.current.touch = performance.now(); }}
      onFocus={(e) => { auto.current.focus = e.target.matches(":focus-visible"); }}
      onBlur={() => { auto.current.focus = false; }}
      data-testid={`${id}-carousel`}
      data-autoplay={running ? "on" : "off"}
    >
      <div
        ref={ref}
        onScroll={update}
        onPointerDown={onDown}
        onClickCapture={onClickCapture}
        tabIndex={0}
        aria-label={label}
        className={`no-scrollbar -mx-5 flex gap-4 overflow-x-auto px-5 pb-2 scroll-px-5 focus:outline-none sm:-mx-8 sm:px-8 sm:scroll-px-8 ${dragging ? "cursor-grabbing select-none" : "cursor-grab"} ${snap}`}
        data-testid={`${id}-carousel-track`}
      >
        {children}
      </div>
      <div className="mt-8 flex items-center gap-5 sm:gap-8">
        <span className="font-mono text-xs tabular-nums text-[#6E685E]" data-testid={`${id}-carousel-counter`}>
          {String(state.idx + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
        </span>
        <div className="relative h-[2px] flex-1 overflow-hidden bg-[#121110]/15" data-testid={`${id}-carousel-progress`}>
          <div className="absolute inset-y-0 left-0 w-full origin-left bg-[#C26D38] transition-transform duration-200 ease-out" style={{ transform: `scaleX(${Math.max(0.06, state.p)})` }} />
        </div>
        <span className="hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E] md:inline-flex">
          <MoveHorizontal className="h-4 w-4" /> {t("prod.drag", "Trascina")}
        </span>
        <div className="flex gap-2">
          {[[-1, ArrowLeft, state.start, "prev"], [1, ArrowRight, state.end, "next"]].map(([dir, Icon, off, k]) => (
            <button
              key={k}
              type="button"
              onClick={() => step(dir)}
              disabled={off}
              aria-label={dir < 0 ? t("car.prev", "Precedente") : t("car.next", "Successivo")}
              className="carousel-arrow tap grid h-12 w-12 place-items-center rounded-full border border-[#121110]/25 text-[#121110] transition-[background-color,color,opacity,transform] duration-300 hover:-translate-y-0.5 hover:border-[#C26D38] hover:bg-[#C26D38] disabled:pointer-events-none disabled:opacity-30"
              data-testid={`${id}-carousel-${k}`}
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
